import { useFormik } from 'formik';
import { useEffect, useState, ReactElement, useRef } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { RegistrationLayout } from './components/RegistrationLayout';

import { Form, Button, InputField, Loader } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePermutive } from '@/hooks/usePermutive';
import useRegistration from '@/hooks/useRegistration';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  getInitDataRegister,
  getNameTeamValid,
  registerUserData,
} from '@/services';
import { Maybe } from '@/types';
import { DataPostRegister, ResponseInitRegister } from '@/types/register';
import {
  TEXTS,
  letterGenderById,
  nameTeamSchema,
  oneSpaceBetweenWordMinTwoChar,
  validateFieldError,
} from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';
import { tracking } from '@/utils/lib';

export const RegistrationNameTeam = () => {
  const toast = useToast();
  const { isDesktop } = useIsDesktop();
  const location = useLocation();
  const [loadingName, setLoadingName] = useState(false);
  const { handleResetRegisterData } = useRegistration();
  const inputRef = useRef<Maybe<HTMLInputElement>>(null);
  const { userInitialData } = useAuth();
  const navigate = useNavigate();

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.nameRegistration,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_posicionportorneo: userInitialData?.posicion_general,
        kw_recibirinfo: userInitialData?.recibeInformacion,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  if (userInitialData) {
    navigate(ROUTES.welcome, {
      replace: true,
    });
  }

  const state: DataPostRegister = location.state;

  const { data: dataInit, isLoading } = useQuery<ResponseInitRegister>(
    'initialDataRegister',
    async () => await getInitDataRegister(),
    {
      refetchOnWindowFocus: false,
      onError: (err) => {
        const error = err as Error;
        toast.showError(error);
      },
    }
  );

  const { mutateAsync: saveRegistration } = useMutation(
    (formData: DataPostRegister) => {
      return registerUserData(formData, !!formData?.tipoescuela, 3);
    }
  );

  const {
    isSubmitting,
    handleSubmit,
    isValid,
    errors,
    handleBlur,
    values,
    touched,
    status,
    setFieldValue,
    setFieldError,
    setStatus,
    setFieldTouched,
  } = useFormik({
    initialValues: {
      nameTeam: dataInit && dataInit.estado ? dataInit.nombreEquipo ?? '' : '',
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    validationSchema: nameTeamSchema,
    onSubmit: async (val: { nameTeam: string }, { setSubmitting }) => {
      const payload = { ...state, nombreEquipo: val.nameTeam };
      try {
        const response = await saveRegistration(payload);
        setSubmitting(false);
        setLoadingName(false);

        if (dataInit) {
          if (response && response.estado) {
            // Reseteo todo el proceso de guardado de
            // datos de registro
            handleResetRegisterData();

            tracking().trackFinRegistracion(response);

            // Esto fue hecho para que se recargue la página
            // Hay un bug que no toma los cookies de sesión
            // Y la unica solucion que se encontro fue esta.
            window.location.reload();
            // Y luego el archivo Authenticated.tsx se encarga de hacer el redirect
          } else {
            toast.show(response?.mensaje);
          }
        } else {
          toast.show(TEXTS.defaultError);
        }
      } catch (err) {
        const error = err as Error;
        toast.showError(error);
      }
    },
  });

  const MIN_CHARACTERS_TO_VALIDATE_NAME = 2;

  const handleNameValidation = async (value: string) => {
    if (value.trim().length >= MIN_CHARACTERS_TO_VALIDATE_NAME) {
      try {
        const response = await getNameTeamValid(encodeURIComponent(value));
        if (response && response.estado) {
          if (!response.utilizable) {
            setFieldError('nameTeam', TEXTS.nameTeamInvalid);
            setStatus(false);
          } else {
            setFieldError('nameTeam', undefined);
            setStatus(true);
          }
        } else {
          setFieldError('nameTeam', TEXTS.errorRequestValidationName);
          setStatus(false);
        }
      } catch (error) {
        setFieldError('nameTeam', TEXTS.errorRequestValidationName);
        setStatus(false);
      }
    }
  };

  useEffect(() => {
    if (dataInit?.estado) {
      setFieldValue('nameTeam', dataInit?.nombreEquipo || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInit]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = oneSpaceBetweenWordMinTwoChar(e.target.value);

    setFieldValue('nameTeam', value);
    setFieldTouched('nameTeam', true, false);

    if (value.trim().length === 0) {
      setFieldError('nameTeam', TEXTS.minLetters);
      setStatus(false);
    } else {
      handleNameValidation(value.trim());
    }
  };

  const title: ReactElement = !isDesktop ? (
    <span className="block pb-10 lg:pb-8 lg:mt-9">
      elegí el nombre de
      <br />
      tu equipo gran dt
    </span>
  ) : (
    <span className="block pb-10 lg:pb-8 lg:mt-9">
      Elegí un nombre para tu equipo
    </span>
  );

  return (
    <RegistrationLayout context="nameTeam" title={title}>
      {<Loader isVisible={isLoading} timeout={false} />}
      <Form.Form
        className="space-y-8"
        loading={isSubmitting || loadingName}
        onSubmit={handleSubmit}
      >
        <Form.Field>
          <InputField
            ref={inputRef}
            autoComplete="off"
            behavior="default"
            error={validateFieldError(
              touched.nameTeam,
              errors.nameTeam,
              values.nameTeam.length
            )}
            maxLength={30}
            minLength={2}
            name="nameTeam"
            placeholder="Ingresar nombre"
            success={!errors.nameTeam && values.nameTeam?.length > 0 && status}
            type="text"
            value={values.nameTeam}
            onBlur={(e: React.FocusEvent<any, Element>) => {
              if (values.nameTeam.length < 2) {
                handleBlur(e);
              }
            }}
            onChange={handleInputChange}
          />
        </Form.Field>
        <Button
          className="w-full"
          disabled={!isValid || loadingName}
          type="submit"
          variant="primary"
          onClick={handleSubmit}
        >
          Continuar
        </Button>
      </Form.Form>
    </RegistrationLayout>
  );
};
